import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { CancellationWindow, OnboardingCourt, OnboardingTeamMember } from 'modules/arena/onboarding/types';

// A arena "da apresentação". Sem cadastro, é a Arena Praia Norte do mock; quando o visitante
// conclui o onboarding, passa a ser a arena que ELE cadastrou, e as telas do painel (menu,
// agenda, quadras, relatórios) e o marketplace do jogador mostram os dados dele.
// Fica no localStorage do navegador: some com "Recomeçar demonstração".
export interface ArenaProfile {
  arenaName: string;
  unitName: string;
  address: string;
  city: string;
  sports: string[];
  courts: OnboardingCourt[];
  openDays: string[];
  openTime: string;
  closeTime: string;
  team: OnboardingTeamMember[];
  cancellationWindow: CancellationWindow;
  refundPercentage: number;
  planId: string;
  ownerName?: string;
}

export const DEFAULT_ARENA_PROFILE: ArenaProfile = {
  arenaName: 'Arena Praia Norte',
  unitName: 'Unidade Aldeota',
  address: 'Rua Desembargador Leite, 890',
  city: 'Fortaleza-CE',
  sports: ['Beach Tennis', 'Futevôlei', 'Vôlei de Praia', 'Pickleball'],
  courts: [
    { id: 'areia-1', name: 'Areia 1', sportIds: ['Beach Tennis'], pricePerHour: 60 },
    { id: 'areia-2', name: 'Areia 2', sportIds: ['Futevôlei'], pricePerHour: 60 },
    { id: 'areia-3', name: 'Areia 3', sportIds: ['Beach Tennis', 'Vôlei de Praia'], pricePerHour: 80 },
    { id: 'quadra-5', name: 'Quadra 5', sportIds: ['Pickleball'], pricePerHour: 55 },
  ],
  openDays: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
  openTime: '06:00',
  closeTime: '22:00',
  team: [
    { id: 'bruno', name: 'Bruno Lima', role: 'recepcao' },
    { id: 'camila', name: 'Camila Rocha', role: 'instrutor' },
    { id: 'rui', name: 'Rui Almeida', role: 'instrutor' },
  ],
  cancellationWindow: '24h',
  refundPercentage: 100,
  planId: 'profissional',
  ownerName: 'Ana Souza',
};

const STORAGE_KEY = 'marco.arena_profile';
// Chaves de tudo que a demonstração grava, para o "Recomeçar" deixar o navegador zerado.
export const DEMO_STORAGE_KEYS = [STORAGE_KEY, 'marco.session_user', 'marco.demo_tour'];

function readStoredProfile(): ArenaProfile | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? ({ ...DEFAULT_ARENA_PROFILE, ...JSON.parse(raw) } as ArenaProfile) : null;
  } catch {
    return null;
  }
}

interface ArenaProfileContextValue {
  profile: ArenaProfile;
  /** true quando o visitante cadastrou a própria arena nesta demonstração. */
  isCustom: boolean;
  saveProfile: (profile: ArenaProfile) => void;
  resetDemo: () => void;
}

const ArenaProfileContext = createContext<ArenaProfileContextValue | undefined>(undefined);

export function ArenaProfileProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<ArenaProfile | null>(readStoredProfile);

  const saveProfile = useCallback((profile: ArenaProfile) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch {
      // modo anônimo/sem storage: a arena vale só até recarregar a página
    }
    setStored(profile);
  }, []);

  const resetDemo = useCallback(() => {
    DEMO_STORAGE_KEYS.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // nada a limpar
      }
    });
    setStored(null);
  }, []);

  const value = useMemo<ArenaProfileContextValue>(
    () => ({ profile: stored ?? DEFAULT_ARENA_PROFILE, isCustom: stored !== null, saveProfile, resetDemo }),
    [stored, saveProfile, resetDemo]
  );

  return <ArenaProfileContext.Provider value={value}>{children}</ArenaProfileContext.Provider>;
}

export function useArenaProfile() {
  const context = useContext(ArenaProfileContext);
  if (!context) throw new Error('useArenaProfile precisa estar dentro de <ArenaProfileProvider>.');
  return context;
}

/** Menor preço por hora entre as quadras (o "a partir de" do marketplace). */
export function lowestCourtPrice(profile: ArenaProfile): number {
  const prices = profile.courts.map((court) => court.pricePerHour).filter((price) => price > 0);
  return prices.length ? Math.min(...prices) : 0;
}
